<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { usePushNudge } from 'dashboard/composables/usePushNudge';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const { mode, shouldShow, requestPermission, promptInstall, dismiss } =
  usePushNudge();

// Banner color per mode: blue to invite, amber to warn (blocked).
const bannerColor = computed(() =>
  mode.value === 'denied' ? 'amber' : 'blue'
);

// `request` and `android-install` expose a native enable button via Banner's
// actionLabel; the other modes only carry guidance text.
const enableLabel = computed(() => {
  if (mode.value === 'request' || mode.value === 'android-install') {
    return t('PUSH_NUDGE.ENABLE');
  }
  return null;
});

const message = computed(() => {
  switch (mode.value) {
    case 'ios-install':
      return t('PUSH_NUDGE.IOS_INSTALL.MESSAGE');
    case 'denied':
      return t('PUSH_NUDGE.DENIED.MESSAGE');
    case 'android-install':
    case 'request':
    default:
      return t('PUSH_NUDGE.REQUEST.MESSAGE');
  }
});
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <Banner
    v-if="shouldShow"
    :color="bannerColor"
    :action-label="enableLabel"
    @action="requestPermission"
  >
    <div class="flex items-center gap-2">
      <span>{{ message }}</span>
      <template v-if="mode === 'ios-install'">
        <span class="font-medium">
          {{ $t('PUSH_NUDGE.IOS_INSTALL.STEPS') }}
        </span>
      </template>
      <Button
        v-if="mode === 'android-install'"
        variant="link"
        color="blue"
        size="sm"
        :label="$t('PUSH_NUDGE.INSTALL_APP')"
        @click="promptInstall"
      />
      <Button
        variant="ghost"
        color="slate"
        size="sm"
        icon="i-lucide-x"
        :aria-label="$t('PUSH_NUDGE.DISMISS')"
        @click="dismiss"
      />
    </div>
  </Banner>
</template>
